import React, { useState } from "react";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import DashboardSection from "../../dashboard/components/DashboardSection";
import DashboardCard from "../../dashboard/components/DashboardCard";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { FlameIconSvg } from "../../../svgFiles/FlameIconSvg";
import { WashIconSvg } from "../../../svgFiles/WashIconSvg";
import { RipeningIconSvg } from "../../../svgFiles/RipeningIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import {
  useGetReworkAreaSummaryQuery,
  useGetReworkPackingSummaryQuery,
  useGetToastedSummaryQuery,
} from "../rework/reworkApi";
import { useGetHumidSummaryQuery } from "../humid/humidApi";
import { useGetGreenRoomSummaryQuery } from "../greenroom/greenRoomApi";

const MetricBox = ({ label, value, unit }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value">
      {value ?? "0"}
      {unit && <span className="db-metric-unit"> {unit}</span>}
    </div>
  </div>
);

const ReworkDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [, setStartDate] = useState(currentDate);
  const [, setEndDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    const single = start === end;
    setIsSingleDay(single);
    setStartDate(start);
    setEndDate(end);
    setDate(start);
    if (single) setLotNumber(makePriorLotNumber(start));
  };

  const { data: reworkSummaryData } = useGetReworkAreaSummaryQuery({
    tab: "rework",
    date,
    lot_number: lotNumber,
  });
  const { data: reworkPackingSummaryData } = useGetReworkPackingSummaryQuery({
    date,
    lot_number: lotNumber,
  });
  const { data: toastedSummaryData } = useGetToastedSummaryQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );
  const { data: humidSummaryData } = useGetHumidSummaryQuery({ date });
  const { data: greenRoomSummaryData } = useGetGreenRoomSummaryQuery({ date });

  // Rework Area Items
  const reworkItems = [
    {
      label: _t("total_prime_kg", "Total Prime KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg || 0,
      unit: " KG",
    },
    {
      label: _t("prime_cheeks_kg", "Prime Cheeks KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg_in_cheeks || 0,
      unit: " KG",
    },
    {
      label: _t("prime_strips_kg", "Prime Strips KG"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg_in_strips || 0,
      unit: " KG",
    },
    {
      label: _t("rejected_kg", "Rejected KG"),
      value: reworkSummaryData?.data?.total_no_of_trash_kg || 0,
      unit: " KG",
    },
    {
      label: _t("seconds_cheeks_kg", "Seconds Cheeks KG"),
      value: reworkSummaryData?.data?.seconds_cheeks_kg || 0,
      unit: " KG",
    },
    {
      label: _t("seconds_strips_kg", "Seconds Strips KG"),
      value: reworkSummaryData?.data?.seconds_strips_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_tapita_kg", "Total Tapita KG"),
      value: reworkSummaryData?.data?.total_tapita_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_pieces_kg", "Total Pieces KG"),
      value: reworkSummaryData?.data?.total_pieces_kg || 0,
      unit: " KG",
    },
  ];

  // Rework Packing Items
  const reworkPackingItems = [
    {
      label: _t("total_boxes", "Total Boxes"),
      value: reworkPackingSummaryData?.data?.total_boxes_packed || 0,
    },
    {
      label: _t("prime_cheek_boxes_10_pound", "Prime Cheek Boxes (10 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_cheek_boxes_10_pound || 0,
    },
    {
      label: _t("prime_cheek_boxes_20_pound", "Prime Cheek Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("prime_strip_boxes_20_pound", "Prime Strip Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_prime_strip_boxes_20_pound || 0,
    },
    {
      label: _t("seconds_cheek_boxes_20_pound", "Seconds Cheek Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_seconds_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("seconds_strip_boxes_20_pound", "Seconds Strip Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_seconds_strip_boxes_20_pound || 0,
    },
    {
      label: _t("tapitas_boxes_20_pound", "Tapitas Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_tapitas_boxes_20_pound || 0,
    },
    {
      label: _t("pieces_boxes_20_pound", "Pieces Boxes (20 pound)"),
      value:
        reworkPackingSummaryData?.data?.total_pieces_boxes_20_pound || 0,
    },
  ];

  // Toasted Items
  const toastedItems = [
    {
      label: _t("total_kg_toasted", "Total KG Toasted"),
      value: (toastedSummaryData?.data?.total_kg_cheeks_in_toasted || 0) + (toastedSummaryData?.data?.total_kg_strips_in_toasted || 0),
      unit: " KG",
    },
    {
      label: _t("total_carts_in_hydrating", "Total Carts in Hydrating"),
      value: toastedSummaryData?.data?.total_carts_in_hydrating || 0,
    },
    {
      label: _t("total_kg_bagged", "Total kg bagged"),
      value: (toastedSummaryData?.data?.total_cheeks_kg_bagged || 0) + (toastedSummaryData?.data?.total_strips_kg_bagged || 0),
      unit: " KG",
    },
  ];

  // Humid Items
  const humidItems = [
    {
      label: _t("total_kg_humid", "Total KG Humid"),
      value: (humidSummaryData?.data?.total_kg_cheeks_in_humidity || 0) + (humidSummaryData?.data?.total_kg_strips_in_humidity || 0),
      unit: " KG",
    },
    {
      label: _t("total_carts_in_oven", "Total Carts in Oven"),
      value: humidSummaryData?.data?.total_carts_in_ovens || 0,
    },
    {
      label: _t("carts_in_cooling_room", "Carts in Cooling Room"),
      value: humidSummaryData?.data?.total_carts_in_cooling_room || 0,
    },
    {
      label: _t("total_kg_bagged", "Total KG Bagged"),
      value: (humidSummaryData?.data?.total_cheeks_kg_bagged || 0) + (humidSummaryData?.data?.total_strips_kg_bagged || 0),
      unit: " KG",
    },
    {
      label: _t("evaporation_prcnt", "Evaporation %"),
      value: humidSummaryData?.data?.evaporation_prcnt || 0,
    },
  ];

  // Sorting Items
  const sortingItems = [
    {
      label: _t("total_kg_prime", "Total Kg Prime"),
      value: reworkSummaryData?.data?.total_no_of_prime_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_kg_trash", "Total Kg Trash"),
      value: reworkSummaryData?.data?.total_no_of_trash_kg || 0,
      unit: " KG",
    },
    {
      label: _t("total_kg_seconds", "Total KG Seconds"),
      value: (reworkSummaryData?.data?.seconds_cheeks_kg || 0) + (reworkSummaryData?.data?.seconds_strips_kg || 0),
      unit: " KG",
    },
  ];

  // Packing Items (for Sorting and Packing section)
  const packingSortingItems = [
    {
      label: _t("total_boxes_packed", "Total Boxes Packed"),
      value: reworkPackingSummaryData?.data?.total_boxes_packed || 0,
    },
    {
      label: _t("total_prime_cheek_boxes", "Total Prime Cheek Boxes"),
      value: (reworkPackingSummaryData?.data?.total_prime_cheek_boxes_10_pound || 0) + (reworkPackingSummaryData?.data?.total_prime_cheek_boxes_20_pound || 0),
    },
    {
      label: _t("total_other_cheek_boxes", "Total Other Cheek Boxes"),
      value: reworkPackingSummaryData?.data?.total_seconds_cheek_boxes_20_pound || 0,
    },
    {
      label: _t("total_prime_strips_boxes", "Total Prime Strips Boxes"),
      value: reworkPackingSummaryData?.data?.total_prime_strip_boxes_20_pound || 0,
    },
    {
      label: _t("total_other_strips_boxes", "Total Other Strips Boxes"),
      value: reworkPackingSummaryData?.data?.total_seconds_strip_boxes_20_pound || 0,
    },
  ];

  // Metrics Items
  const metricsItems = [
    {
      label: _t("prime_cheek_prcnt", "Prime Cheek %"),
      value: 0,
    },
    {
      label: _t("prime_strips_prcnt", "Prime Strips %"),
      value: 0,
    },
    {
      label: _t("total_prime_prcnt", "Total Prime %"),
      value: 0,
    },
    {
      label: _t("total_trash_prcnt", "Total Trash %"),
      value: 0,
    },
    {
      label: _t("total_seconds_prcnt", "Total Seconds %"),
      value: 0,
    },
  ];

  // Green Room Items
  const greenRoomItems = [
    {
      label: _t("total_green_room_cheeks_kg", "Total Green Room Cheeks Kg"),
      value: greenRoomSummaryData?.data?.total_kg_cheeks_in_green_room || 0,
      unit: " KG",
    },
    {
      label: _t("total_green_room_strips_kg", "Total Green Room Strips Kg"),
      value: greenRoomSummaryData?.data?.total_kg_strips_in_green_room || 0,
      unit: " KG",
    },
    {
      label: _t("total_kg_green_room", "Total KG in Green Room"),
      value: (greenRoomSummaryData?.data?.total_kg_cheeks_in_green_room || 0) + (greenRoomSummaryData?.data?.total_kg_strips_in_green_room || 0),
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("rework_overview_title", "Rework Overview")}
        </h5>
        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">
                {_t("lot_number", "Lot Number")}
              </label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">
                  #{lotNumber}
                </div>
              </div>
            </div>
          )}
          <div className="packing_filter_group">
            <label htmlFor="rework-date-range-filter" className="packing_filter_label">
              {_t("date_range", "Date Range")}
            </label>
            <DateRangeFilter
              setDate={setDate}
              onApplyRange={handleApplyRange}
              triggerId="rework-date-range-filter"
            />
          </div>
        </div>
      </div>

      <div>
        {/* ── Rework Area Section ── */}
        <DashboardSection title={_t("rework_area_overview", "Rework Area Overview")}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "4px 0", alignItems: "stretch" }}>

            {/* Left: Rework Area — grouped by theme */}
            <div className="cmn_card" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 18px" }}>

              {/* Prime group — green */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { label: _t("total_prime", "Total Prime"), value: reworkSummaryData?.data?.total_no_of_prime_kg ?? 0 },
                  { label: _t("prime_cheeks", "Prime Cheeks"), value: reworkSummaryData?.data?.total_no_of_prime_kg_in_cheeks ?? 0 },
                  { label: _t("prime_strips", "Prime Strips"), value: reworkSummaryData?.data?.total_no_of_prime_kg_in_strips ?? 0 },
                ].map((item, i) => (
                  <div key={i} className="pp-brokering-box bro-box-green" style={{ borderRadius: 8 }}>
                    <div className="pp-brokering-box-label">{item.label}</div>
                    <div className="pp-brokering-box-value" style={{ color: "#059669" }}>
                      {item.value} <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>kg</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rejected — red */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                <div className="pp-brokering-box bro-box-red" style={{ borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div className="pp-brokering-box-label" style={{ marginBottom: 0, color: "#e53935" }}>{_t("rejected_kg", "Rejected KG")}</div>
                  <div className="pp-brokering-box-value" style={{ color: "#e53935" }}>
                    {reworkSummaryData?.data?.total_no_of_trash_kg ?? 0} <span style={{ fontSize: 11, fontWeight: 500 }}>kg</span>
                  </div>
                </div>
              </div>

              {/* Seconds + Tapita — orange */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { label: _t("seconds_cheeks", "Seconds Cheeks"), value: reworkSummaryData?.data?.seconds_cheeks_kg ?? 0 },
                  { label: _t("seconds_strips", "Seconds Strips"), value: reworkSummaryData?.data?.seconds_strips_kg ?? 0 },
                  { label: _t("tapita", "Tapita"), value: reworkSummaryData?.data?.total_tapita_kg ?? 0 },
                ].map((item, i) => (
                  <div key={i} className="pp-brokering-box bro-box-orange" style={{ borderRadius: 8 }}>
                    <div className="pp-brokering-box-label">{item.label}</div>
                    <div className="pp-brokering-box-value" style={{ color: "#ea6c00" }}>
                      {item.value} <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>kg</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pieces — neutral, full width */}
              <div className="pp-brokering-box bro-box-orange" style={{ borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="pp-brokering-box-label" style={{ marginBottom: 0 }}>{_t("total_pieces", "Total Pieces")}</div>
                <div className="pp-brokering-box-value" style={{ color: "#ea6c00" }}>
                  {reworkSummaryData?.data?.total_pieces_kg ?? 0} <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>kg</span>
                </div>
              </div>

            </div>

            {/* Right: Rework Packing list */}
            <div className="bd-right-col cmn_card">
              <div className="bd-perf-header">{_t("rework_packing", "Rework Packing")}</div>
              <div className="bd-perf-body">
                {reworkPackingItems.map((item, i) => (
                  <div key={i} className="bd-perf-row">
                    <span className="bd-perf-label">{item.label}</span>
                    <span className="bd-perf-value">{item.value ?? "0"}{item.unit ?? ""}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </DashboardSection>
        
          {/* ── Humidity Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                {/* <WashIconSvg size={18} color="#60a5fa" /> */}
                {_t("humidity_room_overview", "Rework Room Overview")}
              </span>
            }
          >
            <div className="db-two-col">
              <DashboardCard title="Humidity Room Overview" icon={<WashIconSvg size={15} color="#667085" />}>
                {humidItems.map((item, i) => (
                  <div key={i} className="db-avg-row">
                    <span>{item.label}</span>
                    <span className="db-avg-val">{item.value ?? "0"}{item.unit && <span className="db-metric-unit">{item.unit}</span>}</span>
                  </div>
                ))}
              </DashboardCard>

              <div className="db-two-col" style={{ gridTemplateColumns: "1fr", gap: 13 }}>
                <DashboardCard title="Toasted Room Overview" icon={<FlameIconSvg size={15} color="#667085" />}>
                  {toastedItems.map((item, i) => (
                    <div key={i} className="db-avg-row">
                      <span>{item.label}</span>
                      <span className="db-avg-val">{item.value ?? "0"}{item.unit && <span className="db-metric-unit">{item.unit}</span>}</span>
                    </div>
                  ))}
                </DashboardCard>

                <DashboardCard title="Green Room Overview" icon={<RipeningIconSvg size={15} color="#667085" />}>
                  {greenRoomItems.map((item, i) => (
                    <div key={i} className="db-avg-row">
                      <span>{item.label}</span>
                      <span className="db-avg-val">{item.value ?? "0"}{item.unit && <span className="db-metric-unit">{item.unit}</span>}</span>
                    </div>
                  ))}
                </DashboardCard>
              </div>
            </div>
          </DashboardSection>

        {/* ── Sorting and Packing Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              {/* <CycleIconSvg size={18} color="#60a5fa" /> */}
              {_t("sorting_and_packing", "Sorting and Packing")}
            </span>
          }
        >
          {/* Sorting Subsection */}
          <div style={{ marginBottom: "16px" }}>
            <div className="db-avg-header" style={{ marginBottom: "12px" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <ScaleIconSvg size={15} color="#10b981" />
                {_t("sorting", "Sorting")}
              </span>
            </div>
            <div className="fqm-grid">
              {sortingItems.map((item, i) => (
                <div key={i} className="pp-brokering-box bro-box-green">
                  <div className="pp-brokering-box-label">{item.label}</div>
                  <div className="pp-brokering-box-value">
                    {item.value ?? "0"}{item.unit ?? ""}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Packing Subsection */}
          <div>
            <div className="db-avg-header" style={{ marginBottom: "12px" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <PackageIconSvg size={15} color="#f97316" />
                {_t("packing", "Packing")}
              </span>
            </div>
            <div className="fqm-grid">
              {packingSortingItems.map((item, i) => (
                <div key={i} className="pp-brokering-box bro-box-orange">
                  <div className="pp-brokering-box-label">{item.label}</div>
                  <div className="pp-brokering-box-value">
                    {item.value ?? "0"}{item.unit ?? ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DashboardSection>
        {/* ── Metrics Section ── */}
        <DashboardSection title={_t("metrics", "Metrics")}>
          <DashboardCard title="Rework Metrics">
            <div className="db-evap-row">
              {metricsItems.map((item, i) => (
                <div key={i} className="db-evap-box">
                  <div className="db-evap-label">{_t(item.label, item.label)}</div>
                  <div className="db-evap-value neutral">{item.value ?? "0"}%</div>
                </div>
              ))}
            </div>
          </DashboardCard>
        </DashboardSection>
      </div>
    </div>
  );
};

export default ReworkDashboard;
